import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { isValidNumber, parse } from 'libphonenumber-js';
import { TranslateService } from '../Services/translate.service';
import { NationalIdentityNumbers } from './nationalIdentityNumbers';

export class GlobalValidator {
	static numberInt(control: UntypedFormControl): ValidationResult {
		if (typeof control.value === 'undefined' || control.value === null || control.value === '') return null;

		const parsed = parseFloat(control.value);
		if (isNaN(control.value) || (parsed | 0) !== parsed) return { incorrectIntegerValue: true };

		return null;
	}

	static numberFloat(control: UntypedFormControl): ValidationResult {
		if (typeof control.value === 'undefined' || control.value === null || control.value === '') return null;

		if (/^\s*$/.test(control.value) || isNaN(control.value)) return { incorrectFloatValue: true };

		return null;
	}

	static mailFormat(control: UntypedFormControl): ValidationResult {
		if (!GlobalValidator.validateAsEmail(control.value as string)) return { incorrectMailFormat: true };

		return null;
	}

	static nationalIdentityFormat(control: UntypedFormControl): ValidationResult {
		if (GlobalValidator.validateAsNationalIdentityNumber(control.value.nin as string, control.value.countryCode)) return null;

		return { incorrectNationalIdentityFormat: true };
	}

	static mobilePhoneFormat(control: UntypedFormControl): ValidationResult | null {
		if (typeof control.value === 'undefined' || control.value === null || control.value === '') return null;

		let num;
		try {
			num = parse(control.value);
			if (!isValidNumber(num)) {
				return { incorrectMobilePhoneFormat: true };
			}
			// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
		} catch (numberParseException) {
			return { incorrectMobilePhoneFormat: true };
		}

		return null;
	}

	static requiredCheckbox(control: UntypedFormControl): ValidationResult {
		if (!control.value) return { incorrectCheckboxValue: true };

		return null;
	}

	static requiredNin(control: UntypedFormControl): ValidationResult {
		if (typeof control.value === 'undefined' || control.value.nin === null || control.value.nin === '') return { required: true };

		return null;
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	static conditionalRequired(conditions: any) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			let required: boolean = false;
			for (const elt in conditions) {
				if (conditions[elt]) required = true;
			}

			if (required && !control.value) {
				return { required: true };
			}
		};
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	static conditionalRequiredNin(conditions: any) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			let required: boolean = true;
			for (const elt in conditions) {
				if (conditions[elt] === false) {
					required = false;
				}
			}

			if (required && !control.value.nin) {
				return { required: true };
			}
		};
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	static equals(equalityObject: { value: any }) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			if (control.value && control.value != equalityObject.value) {
				return { notEqual: true };
			}
		};
	}

	static maxNumber(num: number) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			if (control.value && control.value > num) {
				return { maxNumberValueExceded: true };
			}
		};
	}

	static minNumber(num: number) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			if (control.value && control.value < num) {
				return { minNumberValueExceeded: true };
			}
		};
	}

	static maxLength(length: number) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			if (control.value && control.value.length > length) {
				return { maxValueLengthExceded: true };
			}
		};
	}

	static minLength(length: number) {
		return (control: UntypedFormControl): { [s: string]: boolean } => {
			if (control.value && control.value.length < length) {
				return { minValueLengthExceded: true };
			}
		};
	}

	// ###############################################################################################################
	// Validations
	static validateAsEmail(email: string) {
		const EMAIL_REGEXP = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;
		if (typeof email === 'undefined' || email === null || email === '') return true;

		return email.length > 5 && EMAIL_REGEXP.test(email);
	}

	static validateAsNationalIdentityNumber(number: string, country: string) {
		return NationalIdentityNumbers.Validator.validate(country, number, true);
	}

	static requireAtLeastOne(group: UntypedFormGroup): ValidationResult {
		let valid: boolean = false;
		for (const i in group.controls) {
			if (typeof group.controls[i].value !== 'undefined' && group.controls[i].value !== null && group.controls[i].value !== '') {
				valid = true;
				break;
			}
		}
		if (valid) {
			for (const i in group.controls) {
				if (group.controls[i].getError('requireAtLeastOne')) {
					const errors = group.controls[i].errors;
					delete errors['requireAtLeastOne'];

					let count = 0;
					for (const prop in errors) {
						if (Object.hasOwn(errors, prop)) ++count;
					}
					if (count > 0) {
						group.controls[i].setErrors(errors);
					} else {
						group.controls[i].setErrors(null);
					}
				}
			}

			return null;
		} else {
			for (const i in group.controls) {
				let errors = group.controls[i].errors;
				if (errors === null) errors = {};

				errors['requireAtLeastOne'] = true;
				group.controls[i].setErrors(errors);
			}

			return { requireAtLeastOne: true };
		}
	}

	static defaultErrorMessages = {
		incorrectIntegerValue: 'Default.InputError.IncorrectIntegerValue',
		incorrectFloatValue: 'Default.InputError.IncorrectFloatValue',
		incorrectMailFormat: 'Default.InputError.IncorrectMailFormat',
		incorrectNationalIdentityFormat: 'Default.InputError.IncorrectNationalIdentityFormat',
		incorrectMobilePhoneFormat: 'Default.InputError.IncorrectMobilePhoneFormat',
		incorrectCheckboxValue: 'Default.InputError.IncorrectCheckboxValue',
		maxValueLengthExceded: 'Default.InputError.MaxValueLengthExceded',
		minValueLengthExceded: 'Default.InputError.MinValueLengthExceded',
		maxNumberValueExceded: 'Default.InputError.MaxNumberValueExceded',
		minNumberValueExceeded: 'Default.InputError.MinNumberValueExceeded',
		notEqual: 'Default.InputError.NotEqual',
		required: 'Default.InputError.Required',
		requireAtLeastOne: 'Default.InputError.requireAtLeastOne',
	};

	// ###############################################################################################################
	// Helper functions

	static validateFormControl(
		translateService: TranslateService,
		control: UntypedFormControl,
		errorMessages: { [name: string]: string } | string = 'default',
	): string {
		if (typeof control === 'undefined' || control === null || control.valid || errorMessages === 'none') return null;

		if (errorMessages === 'default') {
			return translateService.get(this.defaultErrorMessages[Object.keys(control.errors)[0]]);
		} else {
			// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration - should be fixed
			for (const k in errorMessages as Object) {
				if (control.hasError(k)) {
					return translateService.get(errorMessages[k]);
				}
			}
			if (typeof errorMessages['default'] !== 'undefined') return translateService.get(errorMessages['default']);
		}

		return null;
	}
}

interface ValidationResult {
	[key: string]: boolean;
}
